import React from 'react';
import { z } from 'zod';
import { gql } from '@apollo/client';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { CurrencyInputField, InputField, CheckboxField } from 'components/form';
import { DialogHeader } from 'components/shared/Dialog';
import { formatPhoneInput } from 'formatters';
import { useNotifications } from 'modules/notification/hooks/useNotifications';

const CREATE_NEW_LENDER_PROFILE = gql`
  mutation ($input: LenderProfileWithAccountInput!) {
    createLenderProfileWithAccount(input: $input) {
      channel
      contactEmail
      contactName
      dealerId
      dynamicsContactSubmission
      id
      insuranceRequirements
      lenderAccountId
      lenderAccountIdPg
      lenderProfileId
      name
      logo
      vendorProfileId
    }
  }
`;

const schema = z.object({
  dealerName: z.string().min(1, 'Required'),
  dealerContactFirstName: z.string().min(1, 'Required'),
  dealerContactLastName: z.string().min(1, 'Required'),
  dealerContactEmail: z.string().email('Valid email is required'),
  dealerContactPhone: z.string().nullable().optional(),
  dealerContactTitle: z.string().nullable().optional(),
  monthlyApplications: z.number().nullable().optional(),
  forcastedApplications: z.number().nullable().optional(),
  description: z.string().nullable().optional(),
  sendInvite: z.boolean(),
});

type AddDealerForm = z.infer<typeof schema>;

export const AddDealerModal = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.AddDealerDialog);

  const account = useSelector((state: any) => state.account);
  const userProfile = useSelector((state: any) => state.userProfile);
  const vp = useSelector((state: any) => state.vp);

  const { showNotification } = useNotifications();

  const [createLenderProfileWithAccount, { loading }] = useMutation(CREATE_NEW_LENDER_PROFILE, {
    context: { authRequired: true },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<AddDealerForm>({
    mode: 'onTouched',
    resolver: zodResolver(schema),
    defaultValues: {
      dealerName: '',
      dealerContactFirstName: '',
      dealerContactLastName: '',
      dealerContactEmail: '',
      dealerContactPhone: null,
      dealerContactTitle: null,
      monthlyApplications: null,
      forcastedApplications: null,
      description: null,
      sendInvite: true,
    },
  });

  const onSubmit = async (formData: AddDealerForm) => {
    await createLenderProfileWithAccount({
      variables: {
        input: {
          name: formData.dealerName,
          dealerContactEmail: formData.dealerContactEmail,
          dealerContactFirstName: formData.dealerContactFirstName,
          dealerContactLastName: formData.dealerContactLastName,
          dealerContactTitle: formData.dealerContactTitle ?? null,
          dealerContactPhone: formData.dealerContactPhone ?? null,
          lenderContactEmail: userProfile.email,
          lenderContactFirstName: userProfile.firstName,
          lenderContactLastName: userProfile.lastName,
          channel: 100,
          accountId: account.dynamicsAccountId,
          portalUrl: null,
          portalUsername: null,
          portalPassword: null,
          averageApprovalRate: null,
          monthlyApplications: formData.monthlyApplications ?? null,
          description: formData.description ?? null,
          vendorProfileId: vp.dynamicsId,
          requestDocumentsMethod: '',
          referredBy: 100,
          interestRates: '',
          lenderTypeCode: 300,
          accountType: 'DEALER',
          lpName: account.name,
          sendInvite: formData.sendInvite,
        },
      },
    });

    data.refetchVPs();
    showNotification('Dealer Added Successfully.');
    reset();
    handleClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogHeader label="Add Dealer" handleClose={handleClose} />

        <DialogContent>
          <Stack component="form" id="add-dealer-form" onSubmit={handleSubmit(onSubmit)} gap={2} pt={1}>
            <InputField
              control={control}
              name="dealerName"
              label="Name"
              inputProps={{ required: true, variant: 'outlined', fullWidth: true }}
            />

            <Stack direction="row" gap={2}>
              <CurrencyInputField
                control={control}
                name="monthlyApplications"
                label="Current Applications Monthly"
                textFieldProps={{ variant: 'outlined', fullWidth: true }}
              />
              <CurrencyInputField
                control={control}
                name="forcastedApplications"
                label="Forecasted Applications Monthly"
                textFieldProps={{ variant: 'outlined', fullWidth: true }}
              />
            </Stack>

            <InputField
              control={control}
              name="description"
              label="Description"
              inputProps={{
                variant: 'outlined',
                fullWidth: true,
                placeholder: "What's needed for you to increase your market share with this dealer?",
              }}
            />

            <Typography variant="h6" component="p" /* fontWeight="bold" */ mt={1}>
              Primary Contact
            </Typography>

            <Stack direction="row" gap={2}>
              <InputField
                control={control}
                name="dealerContactFirstName"
                label="First Name"
                inputProps={{ required: true, variant: 'outlined', fullWidth: true }}
              />
              <InputField
                control={control}
                name="dealerContactLastName"
                label="Last Name"
                inputProps={{ required: true, variant: 'outlined', fullWidth: true }}
              />
            </Stack>

            <InputField
              control={control}
              name="dealerContactTitle"
              label="Title"
              inputProps={{ variant: 'outlined', fullWidth: true }}
            />

            <InputField
              control={control}
              name="dealerContactEmail"
              label="Email"
              inputProps={{ required: true, variant: 'outlined', fullWidth: true }}
            />

            <InputField
              control={control}
              name="dealerContactPhone"
              label="Phone"
              formatter={formatPhoneInput}
              inputProps={{ variant: 'outlined', fullWidth: true }}
            />

            <Stack alignItems="center">
              <CheckboxField control={control} name="sendInvite" label="Send Email Invitation" />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            type="submit"
            form="add-dealer-form"
            variant="contained"
            disabled={loading || !isValid}
            endIcon={loading ? <CircularProgress size={16} /> : null}
          >
            Add Dealer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
